import { useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { stringify } from "qs";

import { AvailableFilter } from "api/requests/getProductsV3";
import { ShopItem } from "custom-types/ShopItem";
import { clearKeysFromQuery } from "hooks/useClearQueryStringKeys";
import parseNextRouterAsPath from "utils/parseNextRouterAsPath";

import ShopNoResults from "components/ShopNoResults";

import AppliedChips from "./AppliedChips";
import DesktopFilterColumnUI from "./DesktopFilterColumnUI";
import DynamicFilters from "./DynamicFilters";
import FulfillmentFilters from "./FulfillmentFilters";
import Header from "./Header";
import MobileFilterModalAndShortcutsUI from "./MobileFilterModalAndShortcutsUI";
import ShopItemResults from "./ShopItemResults";

export type UnifiedShopProps = {
  shopItems: ShopItem[];
  availableFilters: AvailableFilter[];
  totalCount: number;
};

export const UNIFIED_SHOP_PAGE_SIZE = 15;

const UnifiedShop: React.FC<UnifiedShopProps> = ({
  shopItems,
  totalCount,
  availableFilters,
}) => {
  const { asPath } = useRouter();
  const { path, query } = parseNextRouterAsPath(asPath);
  const [openFilterSection, setOpenFilterSection] = useState<string>();

  return (
    <>
      <Head>
        <title>Shop Weed</title>
        <meta
          name="description"
          content="Find the best weed with Leafly. Order weed for same-day delivery, find reviews & menus from the top dispensaries."
        />
        <meta name="robots" content="noindex" />
      </Head>

      <Header />

      <section className="container" data-testid="filter-layout">
        <div className="grid grid-cols-1 gap-1 md:gap-8 md:grid-cols-4 md:m-0">
          <div>
            <DesktopFilterColumnUI
              chips={<AppliedChips availableFilters={availableFilters} />}
              filters={
                <>
                  <FulfillmentFilters />
                  <DynamicFilters
                    availableFilters={availableFilters}
                    defaultOpenKey={openFilterSection}
                  />
                </>
              }
            />
            <MobileFilterModalAndShortcutsUI
              availableFilters={availableFilters}
              setOpenFilterSection={setOpenFilterSection}
              filters={
                <>
                  <FulfillmentFilters />
                  <DynamicFilters
                    availableFilters={availableFilters}
                    defaultOpenKey={openFilterSection}
                  />
                </>
              }
              chips={<AppliedChips availableFilters={availableFilters} />}
            />
          </div>

          <div className="md:col-span-3">
            {shopItems.length > 0 ? (
              <ShopItemResults shopItems={shopItems} totalCount={totalCount} />
            ) : (
              <ShopNoResults
                titleText="No matching results"
                subtitleText="Try selecting fewer options or reset all filters"
                wrapperClassName="my-md"
                primaryLinkText="reset filters"
                action={{
                  pathname: path,
                  query: stringify(
                    clearKeysFromQuery(query, ["filter", "fulfillment", "q"]),
                    {
                      arrayFormat: "brackets",
                      indices: false,
                    },
                  ),
                }}
              />
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default UnifiedShop;
